import React, { useState } from "react";
import {
  Table,
  Button,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Select,
  Stack,
  Card,
  Title,
  Pagination,
} from "@mantine/core";
import { IconEye, IconPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAwardSheets } from "../hooks";
import { SignStatusBadge } from "../components/SignStatusBadge";
import { RoleGuard } from "../components/RoleGuard";

export const AwardSheetList: React.FC = () => {
  const navigate = useNavigate();
  const { data: awardSheets, isLoading } = useAwardSheets();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredAwardSheets =
    awardSheets?.filter((sheet) => {
      const matchesSearch =
        sheet.tenderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sheet.vendor.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || sheet.status === statusFilter;
      return matchesSearch && matchesStatus;
    }) || [];

  const paginatedSheets = filteredAwardSheets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "MVR",
    }).format(amount);
  };

  if (isLoading) {
    return <div>Loading award sheets...</div>;
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Award Sheets</Title>
        <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT"]}>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate("/bid-committee/award-sheets/new")}
          >
            New Award Sheet
          </Button>
        </RoleGuard>
      </Group>

      <Card>
        <Group gap="md" mb="md">
          <TextInput
            placeholder="Search by tender number or vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by status"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || "all")}
            data={[
              { value: "all", label: "All Status" },
              { value: "PENDING", label: "Pending" },
              { value: "APPROVED", label: "Approved" },
              { value: "REJECTED", label: "Rejected" },
            ]}
            style={{ width: 200 }}
          />
        </Group>

        <Table>
          <thead>
            <tr>
              <th>Tender Number</th>
              <th>Vendor</th>
              <th>Cost</th>
              <th>Duration</th>
              <th>Signatories</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSheets.map((sheet) => (
              <tr key={sheet.id}>
                <td>
                  <Text fw={500}>{sheet.tenderNumber}</Text>
                </td>
                <td>{sheet.vendor}</td>
                <td>{formatCurrency(sheet.proposedCost)}</td>
                <td>{sheet.proposedDuration} days</td>
                <td>
                  <Group gap="xs">
                    {(sheet.approvals?.length ? sheet.approvals : sheet.signatories).map(
                      (entry) => (
                        <SignStatusBadge
                          key={entry.id}
                          status={entry.status}
                          size="xs"
                        />
                      )
                    )}
                  </Group>
                </td>
                <td>{formatDate(sheet.createdAt)}</td>
                <td>
                  <ActionIcon
                    color="blue"
                    onClick={() =>
                      navigate(`/bid-committee/award-sheets/${sheet.id}`)
                    }
                  >
                    <IconEye size={16} />
                  </ActionIcon>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {filteredAwardSheets.length > itemsPerPage && (
          <Group justify="center" mt="md">
            <Pagination
              total={Math.ceil(filteredAwardSheets.length / itemsPerPage)}
              value={currentPage}
              onChange={setCurrentPage}
            />
          </Group>
        )}
      </Card>
    </Stack>
  );
};
